<template>
    <PageHeader :title="$t('setup_edit.title')" @back="goBack" />

    <div class="flex-1 space-y-6 overflow-y-auto p-4">
        <Panel size="full" padding="p-5">
            <form @submit.prevent="handleSubmit" class="space-y-6">
                <div>
                    <label for="setupName" class="mb-2 block text-sm font-medium text-white">
                        {{ $t('setup_edit.name_label') }}
                    </label>
                    <input
                        id="setupName"
                        v-model="setupName"
                        name="setupName"
                        type="text"
                        required
                        maxlength="50"
                        class="bg-card w-full border border-zinc-700 px-4 py-3 text-white transition-colors focus:border-red-500 focus:outline-none"
                        :style="{ clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)' }"
                        :placeholder="$t('setup_edit.name_placeholder')"
                    />
                    <p class="mt-1 text-xs text-gray-400">{{ $t('setup_edit.char_count', { n: setupName.length }) }}</p>
                </div>

                <div v-if="editProducts.dart">
                    <label class="mb-2 block text-sm font-medium text-white">{{ $t('setup_edit.dart_label') }}</label>
                    <div class="border-stroke-1 border">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="editProducts.dart.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(editProducts.dart.image_url)"
                                    :alt="editProducts.dart.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="truncate text-sm text-white">
                                    {{ editProducts.dart.name }}
                                </div>
                            </div>
                            <button
                                type="button"
                                @click="selectComponent('dart')"
                                class="shrink-0 text-gray-400 hover:text-white"
                                :aria-label="$t('setup_edit.change_dart')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                    />
                                    <path
                                        d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="dartSetupStore.formData.dart_comment"
                                @input="
                                    dartSetupStore.setComponentComment('dart', $event.target.value)
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                </div>

                <template v-else>
                    <div>
                        <label class="mb-2 block text-sm font-medium text-white">{{ $t('setup_edit.point_label') }}</label>
                        <div v-if="editProducts.tip" class="border-stroke-1 border">
                            <div class="bg-bg flex w-full items-center gap-4 p-4">
                                <div
                                    v-if="editProducts.tip.image_url"
                                    class="h-16 w-16 shrink-0 overflow-hidden rounded"
                                >
                                    <img
                                        :src="getImageUrl(editProducts.tip.image_url)"
                                        :alt="editProducts.tip.name"
                                        class="h-full w-full bg-white object-contain p-1"
                                    />
                                </div>
                                <div
                                    v-else
                                    class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                                >
                                    <svg
                                        class="h-8 w-8 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="truncate text-sm text-white">
                                        {{ editProducts.tip.name }}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    @click="selectComponent('tip')"
                                    class="shrink-0 text-gray-400 hover:text-white"
                                    :aria-label="$t('setup_edit.change_point')"
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                    >
                                        <path
                                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                        />
                                        <path
                                            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                                <input
                                    :value="dartSetupStore.formData.tip_comment"
                                    @input="
                                        dartSetupStore.setComponentComment(
                                            'tip',
                                            $event.target.value,
                                        )
                                    "
                                    type="text"
                                    maxlength="255"
                                    :placeholder="$t('setup_create.comment_placeholder')"
                                    class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                                />
                            </div>
                        </div>
                        <button
                            v-else
                            type="button"
                            @click="selectComponent('tip')"
                            class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border p-4 text-left"
                        >
                            <div class="flex-1">
                                <div class="text-sm text-gray-400">{{ $t('setup_edit.select_point') }}</div>
                            </div>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                class="text-gray-400"
                            >
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>

                    <div>
                        <label class="mb-2 block text-sm font-medium text-white">{{ $t('setup_edit.barrel_label') }}</label>
                        <div v-if="editProducts.barrel" class="border-stroke-1 border">
                            <div class="bg-bg flex w-full items-center gap-4 p-4">
                                <div
                                    v-if="editProducts.barrel.image_url"
                                    class="h-16 w-16 shrink-0 overflow-hidden rounded"
                                >
                                    <img
                                        :src="getImageUrl(editProducts.barrel.image_url)"
                                        :alt="editProducts.barrel.name"
                                        class="h-full w-full bg-white object-contain p-1"
                                    />
                                </div>
                                <div
                                    v-else
                                    class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                                >
                                    <svg
                                        class="h-8 w-8 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="truncate text-sm text-white">
                                        {{ editProducts.barrel.name }}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    @click="selectComponent('barrel')"
                                    class="shrink-0 text-gray-400 hover:text-white"
                                    :aria-label="$t('setup_edit.change_barrel')"
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                    >
                                        <path
                                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                        />
                                        <path
                                            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                                <input
                                    :value="dartSetupStore.formData.barrel_comment"
                                    @input="
                                        dartSetupStore.setComponentComment(
                                            'barrel',
                                            $event.target.value,
                                        )
                                    "
                                    type="text"
                                    maxlength="255"
                                    :placeholder="$t('setup_create.comment_placeholder')"
                                    class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                                />
                            </div>
                        </div>
                        <button
                            v-else
                            type="button"
                            @click="selectComponent('barrel')"
                            class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border p-4 text-left"
                        >
                            <div class="flex-1">
                                <div class="text-sm text-gray-400">{{ $t('setup_edit.select_barrel') }}</div>
                            </div>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                class="text-gray-400"
                            >
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>

                    <div>
                        <label class="mb-2 block text-sm font-medium text-white">{{ $t('setup_edit.shaft_label') }}</label>
                        <div v-if="editProducts.shaft" class="border-stroke-1 border">
                            <div class="bg-bg flex w-full items-center gap-4 p-4">
                                <div
                                    v-if="editProducts.shaft.image_url"
                                    class="h-16 w-16 shrink-0 overflow-hidden rounded"
                                >
                                    <img
                                        :src="getImageUrl(editProducts.shaft.image_url)"
                                        :alt="editProducts.shaft.name"
                                        class="h-full w-full bg-white object-contain p-1"
                                    />
                                </div>
                                <div
                                    v-else
                                    class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                                >
                                    <svg
                                        class="h-8 w-8 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="truncate text-sm text-white">
                                        {{ editProducts.shaft.name }}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    @click="selectComponent('shaft')"
                                    class="shrink-0 text-gray-400 hover:text-white"
                                    :aria-label="$t('setup_edit.change_shaft')"
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                    >
                                        <path
                                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                        />
                                        <path
                                            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                                <input
                                    :value="dartSetupStore.formData.shaft_comment"
                                    @input="
                                        dartSetupStore.setComponentComment(
                                            'shaft',
                                            $event.target.value,
                                        )
                                    "
                                    type="text"
                                    maxlength="255"
                                    :placeholder="$t('setup_create.comment_placeholder')"
                                    class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                                />
                            </div>
                        </div>
                        <button
                            v-else
                            type="button"
                            @click="selectComponent('shaft')"
                            class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border p-4 text-left"
                        >
                            <div class="flex-1">
                                <div class="text-sm text-gray-400">{{ $t('setup_edit.select_shaft') }}</div>
                            </div>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                class="text-gray-400"
                            >
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>

                    <div>
                        <label class="mb-2 block text-sm font-medium text-white">{{ $t('setup_edit.flight_label') }}</label>
                        <div v-if="editProducts.flight" class="border-stroke-1 border">
                            <div class="bg-bg flex w-full items-center gap-4 p-4">
                                <div
                                    v-if="editProducts.flight.image_url"
                                    class="h-16 w-16 shrink-0 overflow-hidden rounded"
                                >
                                    <img
                                        :src="getImageUrl(editProducts.flight.image_url)"
                                        :alt="editProducts.flight.name"
                                        class="h-full w-full bg-white object-contain p-1"
                                    />
                                </div>
                                <div
                                    v-else
                                    class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                                >
                                    <svg
                                        class="h-8 w-8 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="truncate text-sm text-white">
                                        {{ editProducts.flight.name }}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    @click="selectComponent('flight')"
                                    class="shrink-0 text-gray-400 hover:text-white"
                                    :aria-label="$t('setup_edit.change_flight')"
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                    >
                                        <path
                                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                        />
                                        <path
                                            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                                <input
                                    :value="dartSetupStore.formData.flight_comment"
                                    @input="
                                        dartSetupStore.setComponentComment(
                                            'flight',
                                            $event.target.value,
                                        )
                                    "
                                    type="text"
                                    maxlength="255"
                                    :placeholder="$t('setup_create.comment_placeholder')"
                                    class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                                />
                            </div>
                        </div>
                        <button
                            v-else
                            type="button"
                            @click="selectComponent('flight')"
                            class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border p-4 text-left"
                        >
                            <div class="flex-1">
                                <div class="text-sm text-gray-400">{{ $t('setup_edit.select_flight') }}</div>
                            </div>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                class="text-gray-400"
                            >
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>
                </template>

                <div class="pb-6">
                    <div class="flex items-center justify-between pb-4">
                        <h4 class="text-md font-bold uppercase">{{ $t('setup_edit.default_toggle_label') }}</h4>
                        <Toggle v-model="isDefaultSetup" class="cursor-pointer"  data-testid="setup-default-toggle"/>
                    </div>
                    <p class="font-sans text-sm leading-normal font-normal tracking-tight">
                        {{ $t('setup_edit.default_toggle_description') }}
                    </p>
                </div>

                <div
                    v-if="errorMessage"
                    class="border border-red-600 bg-red-900/50 p-4"
                    :style="{ clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)' }"
                >
                    <div class="text-sm text-red-300">{{ errorMessage }}</div>
                </div>

                <div class="flex gap-3 pt-2">
                    <DiagonalCutButton
                        type="button"
                        variant="secondary"
                        cut-position="left-top"
                        @click="goBack"
                        :disabled="isSaving"
                        :full-width="true"
                        data-testid="setup-edit-cancel"
                    >
                        {{ $t('general.cancel') }}
                    </DiagonalCutButton>
                    <DiagonalCutButton
                        type="submit"
                        :disabled="isSaving || !isFormValid"
                        :loading="isSaving"
                        variant="primary"
                        cut-position="right-top"
                        :full-width="true"
                        data-testid="setup-edit-submit"
                    >
                        {{ isSaving ? $t('setup_edit.updating') : $t('setup_edit.update_button') }}
                    </DiagonalCutButton>
                </div>
            </form>
        </Panel>
    </div>

    <Modal
        :is-visible="showUnsavedChangesModal"
        :on-confirm="confirmLeaveWithoutSaving"
        :on-decline="() => (showUnsavedChangesModal = false)"
    >
        <p>{{ $t('setup_edit.unsaved_changes') }}</p>
    </Modal>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useDartSetupStore } from '@/stores/dartSetup.js'
import PageHeader from '@/components/PageHeader.vue'
import Panel from '@/components/layout/Panel.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import Toggle from '@/components/ui/Form/Toggle.vue'
import Modal from '@/components/Modal.vue'

export default {
    name: 'SetupEditView',
    components: {
        PageHeader,
        Panel,
        DiagonalCutButton,
        Toggle,
        Modal,
    },
    computed: {
        ...mapStores(useAuthStore, useDartSetupStore),
        setupId() {
            return this.currentSetupId || this.$route.params.id
        },
        setup() {
            if (!this.authStore.dart_setups) return null
            return this.authStore.dart_setups.find((s) => s.id === this.setupId)
        },
        setupName: {
            get() {
                return this.dartSetupStore.formData.name
            },
            set(value) {
                this.dartSetupStore.setFormData({ name: value })
            },
        },
        editProducts() {
            return this.dartSetupStore.selectedProducts
        },
        isDefaultSetup: {
            get() {
                return this.dartSetupStore.isDefaultSetup
            },
            set(value) {
                this.dartSetupStore.setIsDefaultSetup(value)
            },
        },
        isFormValid() {
            return (
                this.setupName.trim() &&
                (this.editProducts.dart ||
                    this.editProducts.tip ||
                    this.editProducts.barrel ||
                    this.editProducts.shaft ||
                    this.editProducts.flight)
            )
        },
        hasChanges() {
            if (!this.setup) return false
            if (this.setupName !== this.setup.name) return true
            if (this.isDefaultSetup !== (this.setup.is_default || false)) return true

            if (this.editProducts.dart) {
                return this.editProducts.dart?.id !== (this.setup.dart?.id || this.setup.dart_id)
            }

            return (
                this.editProducts.tip?.id !== (this.setup.tip?.id || this.setup.tip_id) ||
                this.editProducts.barrel?.id !== (this.setup.barrel?.id || this.setup.barrel_id) ||
                this.editProducts.shaft?.id !== (this.setup.shaft?.id || this.setup.shaft_id) ||
                this.editProducts.flight?.id !== (this.setup.flight?.id || this.setup.flight_id) ||

                this.dartSetupStore.formData.dart_comment?.trim() !== (this.setup.dart_comment || '') ||
                this.dartSetupStore.formData.tip_comment?.trim() !== (this.setup.tip_comment || '') ||
                this.dartSetupStore.formData.barrel_comment?.trim() !== (this.setup.barrel_comment || '') ||
                this.dartSetupStore.formData.shaft_comment?.trim() !== (this.setup.shaft_comment || '') ||
                this.dartSetupStore.formData.flight_comment?.trim() !== (this.setup.flight_comment || '')
            )
        },
    },
    data() {
        return {
            isSaving: false,
            errorMessage: '',
            currentSetupId: null,
            showUnsavedChangesModal: false,
        }
    },
    watch: {
        '$route.query': {
            immediate: true,
            async handler(newQuery) {
                if (newQuery.component && newQuery.productId) {
                    await this.dartSetupStore.setComponent(
                        newQuery.component,
                        parseInt(newQuery.productId),
                    )
                    const setupId = this.$route.params.id
                    if (setupId) {
                        this.$router.replace({
                            name: 'setup-edit',
                            params: { id: setupId },
                        })
                    }
                }
            },
        },
    },
    beforeRouteLeave(to, from, next) {
        // Set flag if going to product selection (don't clear data)
        if (to.name === 'setup-select-product') {
            this.dartSetupStore.setReturningFromProductSelection(true)
        } else if (to.name === 'setup-detail' || to.name === 'setups') {
            // Only clear when navigating away from edit (not just selecting products)
            this.dartSetupStore.clearDraft()
        }
        next()
    },
    mounted() {
        this.currentSetupId = this.$route.params.id

        if (!this.setup) {
            this.$router.push({ name: 'setups' })
            return
        }

        if (
            !this.dartSetupStore?.selectedProducts?.tip &&
            !this.dartSetupStore?.selectedProducts?.dart
        ) {
            this.dartSetupStore.setFormData({
                name: this.setup.name,
                dart_comment: this.setup.dart_comment || '',
                tip_comment: this.setup.tip_comment || '',
                barrel_comment: this.setup.barrel_comment || '',
                shaft_comment: this.setup.shaft_comment || '',
                flight_comment: this.setup.flight_comment || '',
            })
            this.dartSetupStore.setIsDefaultSetup(this.setup.is_default || false)
            this.dartSetupStore.selectedProducts = {
                dart: this.setup.dart || null,
                tip: this.setup.tip || null,
                barrel: this.setup.barrel || null,
                shaft: this.setup.shaft || null,
                flight: this.setup.flight || null,
            }
        }
    },
    methods: {
        async handleSubmit() {
            if (this.isSaving || !this.isFormValid) {
                return
            }

            this.isSaving = true
            this.errorMessage = ''

            try {
                const updateData = {
                    name: this.setupName.trim(),
                    dart_id: this.editProducts.dart?.id || null,
                    tip_id: this.editProducts.tip?.id || null,
                    barrel_id: this.editProducts.barrel?.id || null,
                    shaft_id: this.editProducts.shaft?.id || null,
                    flight_id: this.editProducts.flight?.id || null,
                    dart_comment: this.dartSetupStore.formData.dart_comment?.trim() || null,
                    tip_comment: this.dartSetupStore.formData.tip_comment?.trim() || null,
                    barrel_comment: this.dartSetupStore.formData.barrel_comment?.trim() || null,
                    shaft_comment: this.dartSetupStore.formData.shaft_comment?.trim() || null,
                    flight_comment: this.dartSetupStore.formData.flight_comment?.trim() || null,
                    is_default: this.isDefaultSetup,
                }

                await this.authStore.updateSetup(this.setupId, updateData)

                this.$toast(this.$t('setup_edit.success_updated'), 3000)
                this.dartSetupStore.clearDraft()
                this.$router.push({ name: 'setups' })
            } catch (error) {
                console.error('Failed to update setup:', error)
                if (error.response?.data?.errors) {
                    const errors = error.response.data.errors
                    const firstError = Object.values(errors)[0]
                    this.errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
                } else if (error.response?.data?.message) {
                    this.errorMessage = error.response.data.message
                } else {
                    this.errorMessage = this.$t('setup_edit.error_update')
                }
            } finally {
                this.isSaving = false
            }
        },
        selectComponent(component) {
            this.$router.replace({
                name: 'setup-select-product',
                query: {
                    component,
                    return: 'setup-edit',
                    setupId: this.setupId,
                },
            })
        },
        getImageUrl(imageUrl) {
            if (!imageUrl) return ''
            if (imageUrl.startsWith('http')) return imageUrl
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
            const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '')
            return `${baseUrl}/${imageUrl}`
        },
        goBack() {
            if (this.hasChanges) {
                this.showUnsavedChangesModal = true
            } else {
                this.returnToEntryPoint()
            }
        },
        confirmLeaveWithoutSaving() {
            this.showUnsavedChangesModal = false
            this.returnToEntryPoint()
        },
        // Falls back to setup-detail (the common case) if no entry point was
        // set - e.g. a direct URL navigation to the edit page. Uses replace (not
        // push) so this detour collapses back into a single history slot.
        returnToEntryPoint() {
            const entryPoint = this.dartSetupStore.entryPoint
            this.dartSetupStore.clearEntryPoint()

            if (entryPoint?.type === 'route') {
                this.$router.replace({ name: entryPoint.name, params: entryPoint.params || {} })
            } else {
                this.$router.replace({ name: 'setup-detail', params: { id: this.setupId } })
            }
        },
    },
}
</script>
